import cn from 'classnames'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Table } from 'core-components/library/list'
import type { IColumn } from 'core-components/library/list/types'
import { PartnerNameWithLink } from 'partner360/components/partnerNameWithLink'
import { getKeyInitiativeStatusTranslationKey } from 'partner360/entities/keyInitiatives/model/status'

import type { ICockpitExportCell, ICockpitExportView } from '../../../../api'
import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  COCKPIT_INITIATIVE_GROUP_OPTIONS,
  COCKPIT_INITIATIVE_TOTAL_ROW_ID,
  enrichCockpitInitiativeRowsWithGeo,
  formatCockpitMoney,
  getCockpitInitiativeHierarchyRows,
  type ICockpitInitiativeHierarchyRow,
  type ICockpitInitiativeRow,
  type TCockpitInitiativeGroupField,
  type TCockpitInitiativesHierarchyOrder,
} from '../../../../lib'
import { selectCockpitGeoMappingItems, selectCockpitPartnerData } from '../../../../model/selectors'
import {
  type TCockpitExportSlot,
  useFillCockpitExportSlot,
} from '../../../components/cockpitExport'
import {
  type ICockpitPartnerLinkParams,
  useCockpitPartnerLinkParams,
  useGeoShortNameById,
  usePartnerNameById,
} from '../../../components/cockpitHierarchyTable'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'
import { CockpitTableColumnTitle } from '../../../components/cockpitTableTitle'
import { COCKPIT_GROUP_BY_LABEL, CockpitTileControls } from '../cockpitTileControls'

import { COLLAPSE_SECONDARY_HIERARCHY_WITH_PRIMARY } from './constants'
import { TRANSLATION_KEYS } from './translations'

import styles from './CockpitInitiativesHierarchy.module.scss'

export interface ICockpitInitiativeHierarchyColumn extends IColumn<ICockpitInitiativeHierarchyRow> {
  isNumeric?: boolean
  /** How the column's cell reads once exported. Absent on a group header — a column that spans
   *  others through their `parentField` and holds no cells of its own. */
  exportCell?: (item: ICockpitInitiativeHierarchyRow) => ICockpitExportCell
}

export interface ICockpitInitiativeHierarchyValueColumnsContext {
  hierarchyRows: ICockpitInitiativeHierarchyRow[]
  isExpandable: (rowId: string) => boolean
}

export type TCockpitInitiativeHierarchyValueColumnsFactory = (
  context: ICockpitInitiativeHierarchyValueColumnsContext
) => ICockpitInitiativeHierarchyColumn[]

interface ICockpitInitiativesHierarchyProps {
  rows: ICockpitInitiativeRow[]
  currency: string
  showGeoHierarchy: boolean
  exportSlot?: TCockpitExportSlot
  /** Names the exported view — each table built on this hierarchy is a view of its own. */
  exportKey?: string
  valueColumnsFactory?: TCockpitInitiativeHierarchyValueColumnsFactory
  /** Rendered at the far end of the Group by row. */
  actions?: ReactNode
}

interface ICockpitHierarchyControls {
  expandedPrimaryRows: ReadonlySet<string>
  expandedSecondaryRows: ReadonlySet<string>
  primaryExpandableRows: ReadonlySet<string>
  secondaryExpandableRows: ReadonlySet<string>
  productChildRows: ReadonlySet<string>
  togglePrimaryRow: (rowId: string) => void
  toggleSecondaryRow: (rowId: string) => void
}

/**
 * What a dimension column shows for one row — the single source of truth behind the cell the
 * user sees and the cell the export carries, so the two can never drift apart.
 */
interface IDimensionCell {
  /** i18n key of the hierarchy root, when the cell heads its column instead of naming a value. */
  rootKey?: string
  /** The GeoMapping node, partner or group name; empty where this row has no dimension value. */
  label: string
  /** Full label retained for hover when the visible Geo label is abbreviated. */
  title?: string
  /** Indentation inside its own column, `0` at that column's root. */
  depth: number
  /** Whether a caret is drawn at all, and whether it stands open. */
  hasChildren: boolean
  isExpanded: boolean
}

const EMPTY_CELL: IDimensionCell = { label: '', depth: 0, hasChildren: false, isExpanded: false }

/** Names the hierarchy-order switch in the export; on screen the two buttons speak for themselves. */
const HIERARCHY_LABEL = 'Hierarchy'

const getGroupTitle = (groupField: TCockpitInitiativeGroupField) =>
  COCKPIT_INITIATIVE_GROUP_OPTIONS.find(({ value }) => value === groupField)?.label ?? 'Product'

const getHierarchyOrders = (
  groupField: TCockpitInitiativeGroupField
): { key: TCockpitInitiativesHierarchyOrder; label: string }[] => {
  const groupTitle = getGroupTitle(groupField)

  return [
    { key: 'geoProduct', label: `Geo > ${groupTitle}` },
    { key: 'productGeo', label: `${groupTitle} > Geo` },
  ]
}

const isGeoRow = (item: ICockpitInitiativeHierarchyRow) =>
  item.level === 'geo' || item.level === 'partner'

const MAX_GEO_INDENT_DEPTH = 6
const GEO_DEPTH_CLASS: Record<number, string> = {
  1: styles.depth1,
  2: styles.depth2,
  3: styles.depth3,
  4: styles.depth4,
  5: styles.depth5,
  6: styles.depth6,
}

const isPrimaryHierarchyEdge = (
  parent: ICockpitInitiativeHierarchyRow,
  child: ICockpitInitiativeHierarchyRow,
  showGeoHierarchy: boolean,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder
) => {
  if (parent.level === 'total') {
    if (!showGeoHierarchy) {
      return true
    }

    return hierarchyOrder === 'geoProduct' ? isGeoRow(child) : child.level === 'product'
  }

  if (!showGeoHierarchy) {
    return parent.level === 'product' && child.level === 'product'
  }

  return hierarchyOrder === 'geoProduct'
    ? isGeoRow(parent) && isGeoRow(child)
    : parent.level === 'product' && child.level === 'product'
}

const asRootCell = (rootKey: string, isExpanded: boolean): IDimensionCell => ({
  rootKey,
  label: '',
  depth: 0,
  hasChildren: true,
  isExpanded,
})

/** The Geo column: the root above the complete GeoMapping tree and its Partner leaves. */
const getGeoCell = (
  item: ICockpitInitiativeHierarchyRow,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder,
  isExpanded: boolean,
  isExpandable: boolean,
  partnerNameById: Map<string, string>,
  geoShortNameById: Map<string, string>
): IDimensionCell => {
  if (item.level === 'total') {
    return asRootCell(TRANSLATION_KEYS.allMarkets, isExpanded)
  }

  if (item.level === 'product') {
    /** Under Product > Geo each product opens into its own markets, so the root repeats there. */
    return hierarchyOrder === 'productGeo' && isExpandable
      ? asRootCell(TRANSLATION_KEYS.allMarkets, isExpanded)
      : EMPTY_CELL
  }

  return {
    label:
      item.level === 'partner'
        ? partnerNameById.get(item.partnerId) || item.partnerId
        : geoShortNameById.get(item.geoKey) || item.geoLabel,
    title: item.level === 'geo' ? item.geoLabel : undefined,
    depth: Math.min(item.geoDepth + 1, MAX_GEO_INDENT_DEPTH),
    hasChildren: isExpandable,
    isExpanded: isExpandable && isExpanded,
  }
}

/** The grouping column: the root above every group, then the groups and their leaves. */
const getProductCell = (
  item: ICockpitInitiativeHierarchyRow,
  showGeoHierarchy: boolean,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder,
  rootKey: string,
  isExpanded: boolean,
  isExpandable: boolean,
  isProductChild: boolean
): IDimensionCell => {
  if (item.level === 'total') {
    return asRootCell(rootKey, isExpanded)
  }

  if (item.level !== 'product') {
    /** Under Geo > Product each geo row opens into its own groups, so the root repeats there. */
    return showGeoHierarchy && hierarchyOrder === 'geoProduct' && isExpandable
      ? asRootCell(rootKey, isExpanded)
      : EMPTY_CELL
  }

  return {
    label: item.groupLabel,
    depth: isProductChild ? 2 : 1,
    hasChildren: isExpandable,
    isExpanded: isExpandable && isExpanded,
  }
}

const toExportCell = (
  cell: IDimensionCell,
  translate: (key: string) => string
): ICockpitExportCell => ({
  text: cell.rootKey ? translate(cell.rootKey) : cell.label,
  depth: cell.depth,
  hasChildren: cell.hasChildren,
  isExpanded: cell.isExpanded,
})

const renderTitle = (title: string, className?: string) => (
  <CockpitTableColumnTitle className={className}>{title}</CockpitTableColumnTitle>
)

const renderTranslatedTitle = (translationKey: string, className?: string) => (
  <CockpitTableColumnTitle className={className}>
    <Translation text={translationKey} span />
  </CockpitTableColumnTitle>
)

const getProductTitle = (item: ICockpitInitiativeHierarchyRow) =>
  item.buStrategicInitiativeTips.length
    ? item.buStrategicInitiativeTips.join('\n')
    : item.groupLabel

const renderHierarchyRoot = (
  item: ICockpitInitiativeHierarchyRow,
  translationKey: string,
  isExpanded: boolean,
  onToggle: (rowId: string) => void
) => (
  <button
    type="button"
    className={styles.toggle}
    onClick={() => onToggle(item.rowId)}
    aria-expanded={isExpanded}
  >
    <span className={styles.caret} aria-hidden="true" />
    <b>
      <Translation text={translationKey} span />
    </b>
  </button>
)

const renderGeoDimension = (
  item: ICockpitInitiativeHierarchyRow,
  cell: IDimensionCell,
  partnerLinkParams: ICockpitPartnerLinkParams,
  onToggle: (rowId: string) => void
) => {
  if (cell.rootKey) {
    return renderHierarchyRoot(item, cell.rootKey, cell.isExpanded, onToggle)
  }

  if (!cell.label) {
    return <span aria-hidden="true" />
  }

  const indentationClassName = GEO_DEPTH_CLASS[cell.depth]

  if (!cell.hasChildren) {
    return (
      <span className={cn(styles.rootLabel, indentationClassName)} title={cell.title ?? cell.label}>
        {item.level === 'partner' ? (
          <PartnerNameWithLink
            className={styles.partnerLink}
            partnerId={item.partnerId}
            partnerName={cell.label}
            fYear={partnerLinkParams.fYear}
            programType={partnerLinkParams.offering}
          />
        ) : (
          cell.label
        )}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={cn(styles.toggle, indentationClassName)}
      onClick={() => onToggle(item.rowId)}
      aria-expanded={cell.isExpanded}
    >
      <span className={styles.caret} aria-hidden="true" />
      <span className={styles.rootLabel} title={cell.title ?? cell.label}>
        {cell.label}
      </span>
    </button>
  )
}

const renderProductDimension = (
  item: ICockpitInitiativeHierarchyRow,
  cell: IDimensionCell,
  showGeoHierarchy: boolean,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder,
  isProductChild: boolean,
  onToggle: (rowId: string) => void
) => {
  if (cell.rootKey) {
    return renderHierarchyRoot(item, cell.rootKey, cell.isExpanded, onToggle)
  }

  if (!cell.label) {
    return <span aria-hidden="true" />
  }

  if (cell.hasChildren) {
    return (
      <button
        type="button"
        className={cn(styles.toggle, {
          [styles.hierarchyRootChild]: item.parentRowId === COCKPIT_INITIATIVE_TOTAL_ROW_ID,
          [styles.productGroup]:
            showGeoHierarchy && hierarchyOrder === 'geoProduct' && !isProductChild,
          [styles.productChild]: isProductChild,
        })}
        onClick={() => onToggle(item.rowId)}
        aria-expanded={cell.isExpanded}
      >
        <span className={styles.caret} aria-hidden="true" />
        <span className={styles.rootLabel} title={cell.title ?? getProductTitle(item)}>
          {cell.label}
        </span>
      </button>
    )
  }

  return (
    <span
      className={cn(styles.childLabel, { [styles.productChild]: isProductChild })}
      title={cell.title ?? getProductTitle(item)}
    >
      {cell.label}
    </span>
  )
}

/** Whether a row's figures are set in bold: the total and every subtotal, but never a partner —
 *  a partner stays a leaf of the Geo tree even where its products hang beneath it. */
export const isEmphasizedHierarchyRow = (
  item: ICockpitInitiativeHierarchyRow,
  isExpandable: boolean
) => item.level === 'total' || (isExpandable && item.level !== 'partner')

export const renderHierarchyValue = (
  item: ICockpitInitiativeHierarchyRow,
  value: string,
  isExpandable: boolean
) => (isEmphasizedHierarchyRow(item, isExpandable) ? <b>{value}</b> : value)

const getColumns = (
  groupField: TCockpitInitiativeGroupField,
  formatMoney: (value: number | null) => string,
  translate: (key: string) => string,
  showGeoHierarchy: boolean,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder,
  partnerNameById: Map<string, string>,
  geoShortNameById: Map<string, string>,
  partnerLinkParams: ICockpitPartnerLinkParams,
  controls: ICockpitHierarchyControls,
  valueColumns?: ICockpitInitiativeHierarchyColumn[]
): ICockpitInitiativeHierarchyColumn[] => {
  const tableKeys = FINANCIAL_COCKPIT_TRANSLATION_KEYS.keyInitiatives.table
  const {
    expandedPrimaryRows,
    expandedSecondaryRows,
    primaryExpandableRows,
    secondaryExpandableRows,
    productChildRows,
    togglePrimaryRow,
    toggleSecondaryRow,
  } = controls
  const groupTitle = getGroupTitle(groupField)
  const productRootTranslationKey =
    groupField === 'development' ? TRANSLATION_KEYS.bu : TRANSLATION_KEYS.overall
  const isGeoPrimary = hierarchyOrder === 'geoProduct'
  const geoExpandedRows = isGeoPrimary ? expandedPrimaryRows : expandedSecondaryRows
  const geoExpandableRows = isGeoPrimary ? primaryExpandableRows : secondaryExpandableRows
  const toggleGeoRow = isGeoPrimary ? togglePrimaryRow : toggleSecondaryRow
  const isProductPrimary = !showGeoHierarchy || hierarchyOrder === 'productGeo'
  const productExpandedRows = isProductPrimary ? expandedPrimaryRows : expandedSecondaryRows
  const productExpandableRows = isProductPrimary ? primaryExpandableRows : secondaryExpandableRows
  const toggleProductRow = isProductPrimary ? togglePrimaryRow : toggleSecondaryRow
  const isExpandable = (rowId: string) =>
    primaryExpandableRows.has(rowId) || secondaryExpandableRows.has(rowId)
  const getGeo = (item: ICockpitInitiativeHierarchyRow) =>
    getGeoCell(
      item,
      hierarchyOrder,
      geoExpandedRows.has(item.rowId),
      geoExpandableRows.has(item.rowId),
      partnerNameById,
      geoShortNameById
    )
  const getProduct = (item: ICockpitInitiativeHierarchyRow) => {
    const cell = getProductCell(
      item,
      showGeoHierarchy,
      hierarchyOrder,
      productRootTranslationKey,
      productExpandedRows.has(item.rowId),
      productExpandableRows.has(item.rowId),
      productChildRows.has(item.rowId)
    )
    if (groupField !== 'status' || cell.rootKey || !cell.label) return cell
    const label = translate(getKeyInitiativeStatusTranslationKey(cell.label))
    return { ...cell, label, title: label }
  }
  const columns: ICockpitInitiativeHierarchyColumn[] = []
  const geoColumn: ICockpitInitiativeHierarchyColumn = {
    field: 'geoDimension',
    title: 'Geo',
    className: cn(styles.dimension, {
      [styles.groupStart]: showGeoHierarchy && hierarchyOrder === 'productGeo',
    }),
    hideSettings: true,
    disableSort: true,
    titleRender: () => renderTitle('Geo'),
    renderFunction: (item) =>
      renderGeoDimension(item, getGeo(item), partnerLinkParams, toggleGeoRow),
    exportCell: (item) => toExportCell(getGeo(item), translate),
  }
  const productColumn: ICockpitInitiativeHierarchyColumn = {
    field: 'productDimension',
    title: groupTitle,
    className: cn(styles.dimension, {
      [styles.groupStart]: showGeoHierarchy && hierarchyOrder === 'geoProduct',
    }),
    hideSettings: true,
    disableSort: true,
    titleRender: () => renderTitle(groupTitle),
    renderFunction: (item) =>
      renderProductDimension(
        item,
        getProduct(item),
        showGeoHierarchy,
        hierarchyOrder,
        productChildRows.has(item.rowId),
        toggleProductRow
      ),
    exportCell: (item) => toExportCell(getProduct(item), translate),
  }

  if (showGeoHierarchy) {
    columns.push(
      ...(hierarchyOrder === 'geoProduct' ? [geoColumn, productColumn] : [productColumn, geoColumn])
    )
  } else {
    columns.push(productColumn)
  }

  if (valueColumns) {
    columns.push(...valueColumns)
    return columns
  }

  columns.push(
    {
      field: 'goal',
      title: tableKeys.goal,
      className: cn(styles.numeric, styles.groupStart),
      isNumeric: true,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTranslatedTitle(tableKeys.goal, styles.numeric),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatMoney(item.goal), isExpandable(item.rowId)),
      exportCell: (item) => ({ text: formatMoney(item.goal), value: item.goal }),
    },
    {
      field: 'incremental',
      title: tableKeys.incremental,
      className: styles.numeric,
      isNumeric: true,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTranslatedTitle(tableKeys.incremental, styles.numeric),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatMoney(item.incremental), isExpandable(item.rowId)),
      exportCell: (item) => ({ text: formatMoney(item.incremental), value: item.incremental }),
    },
    {
      field: 'mdfPlanned',
      title: tableKeys.mdfPlanned,
      className: styles.numeric,
      isNumeric: true,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTranslatedTitle(tableKeys.mdfPlanned, styles.numeric),
      renderFunction: (item) =>
        renderHierarchyValue(item, formatMoney(item.mdfPlanned), isExpandable(item.rowId)),
      exportCell: (item) => ({ text: formatMoney(item.mdfPlanned), value: item.mdfPlanned }),
    },
    {
      field: 'count',
      title: tableKeys.count,
      className: styles.numeric,
      isNumeric: true,
      hideSettings: true,
      disableSort: true,
      titleRender: () => renderTranslatedTitle(tableKeys.count, styles.numeric),
      renderFunction: (item) =>
        renderHierarchyValue(item, String(item.count), isExpandable(item.rowId)),
      exportCell: (item) => ({ text: String(item.count), value: item.count }),
    }
  )

  return columns
}

export const CockpitInitiativesHierarchy = ({
  rows,
  currency,
  showGeoHierarchy,
  exportSlot,
  exportKey = 'initiatives',
  valueColumnsFactory,
  actions,
}: ICockpitInitiativesHierarchyProps) => {
  const [hierarchyOrder, setHierarchyOrder] =
    useState<TCockpitInitiativesHierarchyOrder>('geoProduct')
  const [groupField, setGroupField] = useState<TCockpitInitiativeGroupField>('development')
  const [expandedPrimaryRowIds, setExpandedPrimaryRowIds] = useState<string[]>([
    COCKPIT_INITIATIVE_TOTAL_ROW_ID,
  ])
  const [expandedSecondaryRowIds, setExpandedSecondaryRowIds] = useState<string[]>([])
  const partners = useSelector(selectCockpitPartnerData)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const partnerNameById = usePartnerNameById()
  const geoShortNameById = useGeoShortNameById()
  const partnerLinkParams = useCockpitPartnerLinkParams()
  const translate = useSafetyTranslation()
  const hierarchyOrders = useMemo(() => getHierarchyOrders(groupField), [groupField])

  const hierarchyRows = useMemo(() => {
    const rowsWithGeo = enrichCockpitInitiativeRowsWithGeo(rows, partners)

    return getCockpitInitiativeHierarchyRows(
      rowsWithGeo,
      groupField,
      showGeoHierarchy,
      hierarchyOrder,
      geoMappingItems
    )
  }, [rows, partners, groupField, showGeoHierarchy, hierarchyOrder, geoMappingItems])
  const hierarchyRowsById = useMemo(
    () => new Map(hierarchyRows.map((row) => [row.rowId, row])),
    [hierarchyRows]
  )
  const { primaryExpandableRows, secondaryExpandableRows, productChildRows } = useMemo(() => {
    const primaryRows = new Set<string>()
    const secondaryRows = new Set<string>()
    const productChildren = new Set<string>()

    hierarchyRows.forEach((child) => {
      const parent = child.parentRowId ? hierarchyRowsById.get(child.parentRowId) : undefined

      if (!parent) {
        return
      }

      const expandableRows = isPrimaryHierarchyEdge(parent, child, showGeoHierarchy, hierarchyOrder)
        ? primaryRows
        : secondaryRows

      expandableRows.add(parent.rowId)

      if (parent.level === 'product' && child.level === 'product') {
        productChildren.add(child.rowId)
      }
    })

    return {
      primaryExpandableRows: primaryRows,
      secondaryExpandableRows: secondaryRows,
      productChildRows: productChildren,
    }
  }, [hierarchyRows, hierarchyRowsById, showGeoHierarchy, hierarchyOrder])
  const expandedPrimaryRows = useMemo(() => new Set(expandedPrimaryRowIds), [expandedPrimaryRowIds])
  const expandedSecondaryRows = useMemo(
    () => new Set(expandedSecondaryRowIds),
    [expandedSecondaryRowIds]
  )
  const visibleRows = useMemo(() => {
    return hierarchyRows.filter((row) => {
      let child = row

      while (child.parentRowId) {
        const parent = hierarchyRowsById.get(child.parentRowId)

        if (!parent) {
          return false
        }

        const expandedRows = isPrimaryHierarchyEdge(parent, child, showGeoHierarchy, hierarchyOrder)
          ? expandedPrimaryRows
          : expandedSecondaryRows

        if (!expandedRows.has(parent.rowId)) {
          return false
        }

        child = parent
      }

      return true
    })
  }, [
    hierarchyRows,
    hierarchyRowsById,
    showGeoHierarchy,
    hierarchyOrder,
    expandedPrimaryRows,
    expandedSecondaryRows,
  ])

  const togglePrimaryRow = useCallback(
    (rowId: string) => {
      const isCollapsing = expandedPrimaryRows.has(rowId)

      setExpandedPrimaryRowIds((current) =>
        isCollapsing ? current.filter((id) => id !== rowId) : [...current, rowId]
      )

      if (COLLAPSE_SECONDARY_HIERARCHY_WITH_PRIMARY && isCollapsing) {
        setExpandedSecondaryRowIds((current) => current.filter((id) => id !== rowId))
      }
    },
    [expandedPrimaryRows]
  )
  const toggleSecondaryRow = useCallback((rowId: string) => {
    setExpandedSecondaryRowIds((current) =>
      current.includes(rowId) ? current.filter((id) => id !== rowId) : [...current, rowId]
    )
  }, [])
  const formatMoney = useCallback(
    (value: number | null) => formatCockpitMoney(value, currency),
    [currency]
  )
  const isExpandable = useCallback(
    (rowId: string) => primaryExpandableRows.has(rowId) || secondaryExpandableRows.has(rowId),
    [primaryExpandableRows, secondaryExpandableRows]
  )
  const valueColumns = useMemo(
    () => valueColumnsFactory?.({ hierarchyRows, isExpandable }),
    [valueColumnsFactory, hierarchyRows, isExpandable]
  )
  const columns = useMemo(
    () =>
      getColumns(
        groupField,
        formatMoney,
        translate,
        showGeoHierarchy,
        hierarchyOrder,
        partnerNameById,
        geoShortNameById,
        partnerLinkParams,
        {
          expandedPrimaryRows,
          expandedSecondaryRows,
          primaryExpandableRows,
          secondaryExpandableRows,
          productChildRows,
          togglePrimaryRow,
          toggleSecondaryRow,
        },
        valueColumns
      ),
    [
      groupField,
      formatMoney,
      translate,
      showGeoHierarchy,
      hierarchyOrder,
      partnerNameById,
      geoShortNameById,
      partnerLinkParams,
      expandedPrimaryRows,
      expandedSecondaryRows,
      primaryExpandableRows,
      secondaryExpandableRows,
      productChildRows,
      togglePrimaryRow,
      toggleSecondaryRow,
      valueColumns,
    ]
  )

  /** The rows the user can see, in their order, opened exactly as far as they opened them. Read
   *  off the very columns that draw the table, so the deck cannot disagree with the screen.
   *  Titles are translated here because a value column may title itself with an i18n key; a
   *  plain title comes back from the translation unchanged. A group header holds no cells, so it
   *  travels as the `group` of the columns beneath it. */
  const exportView = useCallback((): ICockpitExportView => {
    const titleByField = new Map(columns.map(({ field, title }) => [field, translate(title)]))
    const cellColumns = columns.flatMap(({ field, parentField, isNumeric, exportCell }) =>
      exportCell ? [{ field, parentField, isNumeric, exportCell }] : []
    )

    return {
      key: exportKey,
      controls: [
        ...(showGeoHierarchy
          ? [
              {
                label: HIERARCHY_LABEL,
                value: hierarchyOrders.find(({ key }) => key === hierarchyOrder)?.label ?? '',
                options: hierarchyOrders.map(({ label }) => label),
              },
            ]
          : []),
        {
          label: COCKPIT_GROUP_BY_LABEL,
          value:
            COCKPIT_INITIATIVE_GROUP_OPTIONS.find(({ value }) => value === groupField)?.label ?? '',
          options: COCKPIT_INITIATIVE_GROUP_OPTIONS.map(({ label }) => label),
        },
      ],
      table: {
        columns: cellColumns.map(({ field, parentField, isNumeric }) => ({
          key: field,
          title: titleByField.get(field) ?? '',
          isNumeric,
          group: parentField ? titleByField.get(parentField) : undefined,
        })),
        rows: visibleRows.map((item) => ({
          id: item.rowId,
          parentId: item.parentRowId ?? undefined,
          isTotal: item.level === 'total',
          isEmphasized: isEmphasizedHierarchyRow(item, isExpandable(item.rowId)),
          cells: Object.fromEntries(
            cellColumns.map((column) => [column.field, column.exportCell(item)])
          ),
        })),
      },
    }
  }, [
    exportKey,
    showGeoHierarchy,
    hierarchyOrder,
    hierarchyOrders,
    groupField,
    columns,
    visibleRows,
    translate,
    isExpandable,
  ])

  useFillCockpitExportSlot(exportSlot, exportView)

  return (
    <div className={styles.root}>
      <CockpitTileControls
        groupOptions={COCKPIT_INITIATIVE_GROUP_OPTIONS}
        selectedGroup={groupField}
        onSelectGroup={setGroupField}
        actions={actions}
      >
        {showGeoHierarchy && (
          <CockpitSegmentedControlAccent>
            <SegmentedControl
              useItemKeys
              selectedItem={hierarchyOrder}
              onItemSelect={setHierarchyOrder}
              size="small"
              variant="veneer"
            >
              {hierarchyOrders.map(({ key, label }) => (
                <SegmentedControl.Item key={key} itemKey={key}>
                  <span>{label}</span>
                </SegmentedControl.Item>
              ))}
            </SegmentedControl>
          </CockpitSegmentedControlAccent>
        )}
      </CockpitTileControls>

      <Table
        items={visibleRows}
        classes={{ body: styles.body }}
        columns={columns}
        keyField="rowId"
        pageSize={visibleRows.length}
        header
        className={styles.table}
      />
    </div>
  )
}

CockpitInitiativesHierarchy.displayName = 'CockpitInitiativesHierarchy'
